import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "../styles/legalPages.css";

const CONTACT_EMAIL = "owlletreading@gmail.com";
const COMPANY_NAME = "Owllet Reading";
const SITE_URL = "owlletreading.com";

function LegalShell({ title, children }) {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="legal-page">
      <div className="legal-header">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={15} /> Back
        </button>
        <h1>{title}</h1>
      </div>
      <div className="legal-body">{children}</div>
      <footer className="legal-footer">
        Questions? Contact us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="legal-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/* ── Privacy Policy ─────────────────────────────────────────────────────── */
export function PrivacyPolicyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p className="legal-intro">
        Last updated:{" "}
        {new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </p>
      <p>
        {COMPANY_NAME} (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;)
        operates {SITE_URL}. This policy explains how we collect, use, and
        protect your personal data in accordance with the UK General Data
        Protection Regulation (UK GDPR) and the Data Protection Act 2018.
      </p>

      <Section title="1. Who we are">
        <p>
          We are the data controller for personal data collected through this
          website. Contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="2. Data we collect">
        <ul>
          <li>
            <strong>Account data:</strong> username, email address (optional),
            password (stored as a one-way hash), age group (above or below 16),
            security question and answer hash.
          </li>
          <li>
            <strong>Registration ID:</strong> a unique Owllet ID (e.g.
            OWLLET-000001) assigned at registration.
          </li>
          <li>
            <strong>Game and learning data:</strong> game results, scores,
            comprehension answers, vocabulary test performance, and reading
            progress.
          </li>
          <li>
            <strong>Usage data:</strong> login dates, streak counters, and
            calendar activity.
          </li>
          <li>
            <strong>Subscription data:</strong> subscription start and end
            dates.
          </li>
          <li>
            <strong>Technical data:</strong> browser type, device type, and IP
            address (held briefly in server logs).
          </li>
        </ul>
      </Section>

      <Section title="3. How we use your data">
        <ul>
          <li>To provide, personalise, and improve the learning service.</li>
          <li>To track progress and display results.</li>
          <li>To manage your account and subscription.</li>
          <li>
            To communicate with you about your account (if an email was
            provided).
          </li>
          <li>To comply with our legal obligations.</li>
        </ul>
      </Section>

      <Section title="4. Legal basis for processing">
        <ul>
          <li>
            <strong>Contract:</strong> processing is necessary to provide the
            service you registered for.
          </li>
          <li>
            <strong>Legitimate interests:</strong> improving the service and
            preventing fraud.
          </li>
          <li>
            <strong>Legal obligation:</strong> complying with UK law.
          </li>
          <li>
            <strong>Consent:</strong> for optional features such as email
            communications.
          </li>
        </ul>
      </Section>

      <Section title="5. Children's data">
        <p>
          We knowingly provide services to users under 16. For users identified
          as under 13, a parent or guardian must provide verifiable consent
          before the account is activated. Children's data is processed with the
          strictest settings by default. We do not use children's data for
          advertising or profiling beyond the educational purpose of the
          service. This service complies with the ICO's Children's Code (Age
          Appropriate Design Code).
        </p>
      </Section>

      <Section title="6. Data sharing">
        <p>We do not sell your personal data. We may share data with:</p>
        <ul>
          <li>
            Hosting and infrastructure providers who process data on our behalf
            under a data processing agreement.
          </li>
          <li>
            AI content generation services (Anthropic Claude) used solely to
            generate educational passages — no personal user data is sent to
            this service.
          </li>
          <li>Law enforcement or regulators where required by law.</li>
        </ul>
      </Section>

      <Section title="7. Data retention">
        <ul>
          <li>
            Account data is kept for as long as your account is active plus 2
            years after closure.
          </li>
          <li>
            Game results and learning data are retained for the lifetime of the
            account.
          </li>
          <li>Server logs are retained for up to 30 days.</li>
        </ul>
      </Section>

      <Section title="8. Your rights">
        <p>Under UK GDPR you have the right to:</p>
        <ul>
          <li>
            <strong>Access</strong> — request a copy of your personal data.
          </li>
          <li>
            <strong>Rectification</strong> — ask us to correct inaccurate data.
          </li>
          <li>
            <strong>Erasure</strong> — ask us to delete your data (&ldquo;right
            to be forgotten&rdquo;).
          </li>
          <li>
            <strong>Restriction</strong> — ask us to limit how we use your data.
          </li>
          <li>
            <strong>Portability</strong> — receive your data in a structured,
            machine-readable format.
          </li>
          <li>
            <strong>Objection</strong> — object to processing based on
            legitimate interests.
          </li>
        </ul>
        <p>
          To exercise any right, email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will
          respond within 30 days. You also have the right to lodge a complaint
          with the{" "}
          <a href="https://ico.org.uk" target="_blank" rel="noreferrer">
            ICO
          </a>{" "}
          (Information Commissioner's Office).
        </p>
      </Section>

      <Section title="9. Security">
        <p>
          Passwords are stored as bcrypt hashes. Data is transmitted over HTTPS.
          We apply rate limiting and bot protections on all authentication
          endpoints.
        </p>
      </Section>

      <Section title="10. Changes to this policy">
        <p>
          We will notify registered users by email (where provided) of any
          material changes. Continued use of the service after changes
          constitutes acceptance.
        </p>
      </Section>
    </LegalShell>
  );
}

/* ── Terms & Conditions ─────────────────────────────────────────────────── */
export function TermsPage() {
  return (
    <LegalShell title="Terms & Conditions">
      <p className="legal-intro">
        Last updated:{" "}
        {new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </p>
      <p>
        By creating an account or using {SITE_URL} you agree to these terms.
        Please read them carefully. These terms are governed by the laws of
        England and Wales.
      </p>

      <Section title="1. Who we are">
        <p>
          {COMPANY_NAME} operates this educational platform. Contact:{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="2. Eligibility">
        <p>
          Users under 13 must have verifiable parental or guardian consent
          before using the service. Users aged 13–15 must have parental
          permission. By registering, you confirm that these conditions are met
          or that you are 16 or over.
        </p>
      </Section>

      <Section title="3. Account">
        <ul>
          <li>
            You are responsible for keeping your login credentials secure.
          </li>
          <li>You must not share your account with others.</li>
          <li>You must provide accurate information during registration.</li>
          <li>
            Accounts are activated by an administrator — we reserve the right to
            refuse or revoke access.
          </li>
        </ul>
      </Section>

      <Section title="4. Subscription">
        <ul>
          <li>Access to premium features requires an active subscription.</li>
          <li>Prices are stated at the point of purchase.</li>
          <li>
            Under the Consumer Contracts Regulations 2013, you have a 14-day
            cooling-off period from the date of purchase, unless you have
            already accessed digital content and waived this right.
          </li>
          <li>
            We reserve the right to change pricing with 30 days' notice to
            active subscribers.
          </li>
        </ul>
      </Section>

      <Section title="5. Acceptable use">
        <p>You must not:</p>
        <ul>
          <li>Attempt to access other users' accounts or data.</li>
          <li>
            Use the service in any way that could damage, overload, or impair
            it.
          </li>
          <li>Use automated scripts or bots against the service.</li>
          <li>
            Use the service for any purpose other than personal educational use.
          </li>
        </ul>
      </Section>

      <Section title="6. Intellectual property">
        <p>
          All original content on this platform (game logic, UI, educational
          content) is owned by
          {COMPANY_NAME}. Book texts displayed are sourced from the public
          domain (Project Gutenberg). Book cover images are sourced from
          open-access repositories. You may not reproduce, distribute, or create
          derivative works from our proprietary content without written
          permission.
        </p>
      </Section>

      <Section title="7. AI-generated content">
        <p>
          Some comprehension passages and questions are generated using AI.
          While we review content for quality, we do not guarantee that
          AI-generated content is entirely accurate or free from error. Please
          report any concerns to {CONTACT_EMAIL}.
        </p>
      </Section>

      <Section title="8. Limitation of liability">
        <p>
          To the extent permitted by law, {COMPANY_NAME} is not liable for any
          indirect, incidental, or consequential loss arising from your use of
          the service. Our total liability shall not exceed the amount you paid
          for the subscription in the three months before the claim. Nothing in
          these terms limits liability for death, personal injury, or fraud.
        </p>
      </Section>

      <Section title="9. Termination">
        <p>
          We may suspend or close your account if you breach these terms. You
          may close your account at any time by contacting us. On closure, we
          will delete your personal data in accordance with our Privacy Policy.
        </p>
      </Section>

      <Section title="10. Changes to terms">
        <p>
          We may update these terms. We will give at least 14 days' notice for
          material changes. Continued use after that date constitutes
          acceptance.
        </p>
      </Section>
    </LegalShell>
  );
}

/* ── Cookie Policy ──────────────────────────────────────────────────────── */
export function CookiePolicyPage() {
  return (
    <LegalShell title="Cookie Policy">
      <p className="legal-intro">
        Last updated:{" "}
        {new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </p>
      <p>
        This policy explains how {COMPANY_NAME} uses cookies and similar
        technologies on {SITE_URL}, in accordance with the Privacy and
        Electronic Communications Regulations (PECR).
      </p>

      <Section title="What are cookies?">
        <p>
          Cookies are small text files stored on your device when you visit a
          website. They help websites remember your preferences and how you use
          them.
        </p>
      </Section>

      <Section title="Cookies we use">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Name / Type</th>
              <th>Purpose</th>
              <th>Duration</th>
              <th>Essential?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Session storage (localStorage)</td>
              <td>
                Stores your login session, user ID, username, age group, theme,
                and Owllet ID so you stay logged in
              </td>
              <td>24 hours</td>
              <td>Yes — the service cannot function without this</td>
            </tr>
            <tr>
              <td>Cookie consent preference</td>
              <td>
                Remembers whether you have accepted or declined optional cookies
              </td>
              <td>1 year</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>Analytics (if enabled)</td>
              <td>
                Anonymised usage statistics to understand how the service is
                used
              </td>
              <td>Session</td>
              <td>No — requires your consent</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section title="Your choices">
        <p>
          When you first visit the site, you will see our cookie banner. You can
          accept or decline optional cookies at that point. Essential cookies
          cannot be disabled as they are required to log in and use the service.
        </p>
        <p>
          You can also control cookies through your browser settings. Note that
          disabling all cookies may prevent you from logging in. For more
          information visit{" "}
          <a
            href="https://www.aboutcookies.org"
            target="_blank"
            rel="noreferrer"
          >
            aboutcookies.org
          </a>
          .
        </p>
      </Section>

      <Section title="Third-party cookies">
        <p>
          If you sign in with Google, Google may set its own cookies. These are
          governed by
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            {" "}
            Google's Privacy Policy
          </a>
          . We do not control Google's cookies.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>We will update this page if our cookie use changes.</p>
      </Section>
    </LegalShell>
  );
}

/* ── Safeguarding Policy ────────────────────────────────────────────────── */
export function SafeguardingPage() {
  return (
    <LegalShell title="Safeguarding Policy">
      <p className="legal-intro">
        Last updated:{" "}
        {new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </p>
      <p>
        {COMPANY_NAME} is committed to safeguarding and promoting the welfare of
        children and young people who use our platform. This policy sets out our
        approach to child protection and online safety.
      </p>

      <Section title="Our commitment">
        <ul>
          <li>The safety and wellbeing of children is our highest priority.</li>
          <li>
            We follow UK safeguarding legislation including the Children Act
            1989 and 2004.
          </li>
          <li>
            We comply with the ICO's Children's Code (Age Appropriate Design
            Code).
          </li>
          <li>
            We design the service with children's best interests as the default.
          </li>
        </ul>
      </Section>

      <Section title="How we protect children on this platform">
        <ul>
          <li>
            <strong>Age verification:</strong> users indicate whether they are
            above or below 16 at registration. Accounts for younger users
            require admin activation.
          </li>
          <li>
            <strong>No public profiles:</strong> users cannot see each other's
            profiles, results, or personal information.
          </li>
          <li>
            <strong>No direct messaging:</strong> there is no chat or messaging
            feature on this platform.
          </li>
          <li>
            <strong>Content safety:</strong> all AI-generated content is
            filtered for age-appropriateness. Book content is sourced from
            classic public domain literature.
          </li>
          <li>
            <strong>Data minimisation:</strong> we collect only what is needed
            to deliver the educational service.
          </li>
          <li>
            <strong>Privacy by default:</strong> children's accounts have the
            most restrictive privacy settings applied automatically.
          </li>
        </ul>
      </Section>

      <Section title="Reporting a concern">
        <p>
          If you have a safeguarding concern — including concerns about a
          child's safety, inappropriate content, or data misuse — please contact
          us immediately:
        </p>
        <p>
          <strong>Email:</strong>{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
        <p>
          If you believe a child is in immediate danger, contact the police
          (999) or the NSPCC helpline on 0808 800 5000.
        </p>
      </Section>

      <Section title="Parental guidance">
        <ul>
          <li>
            We encourage parents and carers to be involved in their child's use
            of this platform.
          </li>
          <li>
            Parents may request deletion of their child's account and data by
            contacting us.
          </li>
          <li>We will respond to all parental data requests within 30 days.</li>
        </ul>
      </Section>

      <Section title="Staff and administrators">
        <p>
          Anyone with administrator access to user data is required to treat
          that data with strict confidentiality. Admin access is limited to
          authorised personnel only.
        </p>
      </Section>

      <Section title="Review">
        <p>
          This policy is reviewed annually and updated as required by changes in
          legislation or best practice.
        </p>
      </Section>
    </LegalShell>
  );
}

/* ── Accessibility Statement ────────────────────────────────────────────── */
export function AccessibilityPage() {
  return (
    <LegalShell title="Accessibility Statement">
      <p className="legal-intro">
        Last updated:{" "}
        {new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </p>
      <p>
        {COMPANY_NAME} is committed to making {SITE_URL} accessible to everyone,
        including users with disabilities. We aim to meet the Web Content
        Accessibility Guidelines (WCAG) 2.1 at Level AA.
      </p>

      <Section title="What we have done">
        <ul>
          <li>Keyboard navigation is supported throughout the application.</li>
          <li>
            Number keys (1–4) can be used to answer vocabulary and MCQ
            questions.
          </li>
          <li>Text-to-speech is available on vocabulary test questions.</li>
          <li>Colour contrast meets WCAG AA standards for standard text.</li>
          <li>Themes can be switched to suit personal preference.</li>
          <li>Font sizes are designed to be readable by younger users.</li>
          <li>
            Interactive elements have descriptive labels and title attributes.
          </li>
        </ul>
      </Section>

      <Section title="Known limitations">
        <ul>
          <li>
            Some game pages may not be fully operable using a screen reader — we
            are actively improving this.
          </li>
          <li>
            AI-generated passage images (where used) may not always have
            meaningful alternative text.
          </li>
          <li>
            The print preview window has not been tested with assistive
            technology.
          </li>
        </ul>
      </Section>

      <Section title="How to report an accessibility issue">
        <p>
          If you find any part of the site difficult to use, or if you need
          content in a different format, please contact us:
        </p>
        <p>
          <strong>Email:</strong>{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
        <p>We aim to respond to accessibility queries within 5 working days.</p>
      </Section>

      <Section title="Enforcement">
        <p>
          If you are not satisfied with our response, you can contact the
          <a
            href="https://www.equalityhumanrights.com"
            target="_blank"
            rel="noreferrer"
          >
            {" "}
            Equality Advisory and Support Service (EASS)
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
}
